import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Plus, Minus, ShoppingBag, Trash2, ArrowLeft, CheckCircle2, AlertCircle, Calendar, MapPin, CreditCard, Lock,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_ORDER = 300;
const CHICAGO_SALES_TAX_RATE = 0.1025; // Flat 10.25% Chicago sales tax

const SQ_ENV = process.env.REACT_APP_SQUARE_ENVIRONMENT || 'sandbox';
const SQ_APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
const SQ_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;
const SQ_SDK_URL = SQ_ENV === 'production'
  ? 'https://web.squarecdn.com/v1/square.js'
  : 'https://sandbox.web.squarecdn.com/v1/square.js';

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRe = /^[0-9+()\-\s]{7,20}$/;

const todayStr = () => new Date().toISOString().slice(0, 10);

// Delivery is free on all catering orders (pickup is free too).
function computeDelivery() {
  return 0;
}

export default function CartSheet({ open, onOpenChange, cart, updateQty, clearCart }) {
  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const belowMin = subtotal > 0 && subtotal < MIN_ORDER;

  // step: 'cart' | 'details' | 'payment' | 'done'
  const [step, setStep] = useState('cart');
  const [confirmation, setConfirmation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '',
    date: '', time: '12:00', headcount: '',
    fulfillment: 'delivery',
    delivery_address: '', dietary_notes: '',
    tip_choice: 'none', tip_custom: '',
  });
  const [errors, setErrors] = useState({});

  const delivery = computeDelivery();
  // Automatic 10% Large Group Discount for 50+ guests.
  const discount = subtotal > 0 && Number(form.headcount) >= 50
    ? Math.round(subtotal * 0.10 * 100) / 100 : 0;
  // Tip is calculated on the discounted subtotal.
  const tipBase = subtotal - discount;
  const tip = form.tip_choice === 'custom'
    ? Math.max(0, Math.round((parseFloat(form.tip_custom) || 0) * 100) / 100)
    : ['10', '15', '20'].includes(form.tip_choice)
      ? Math.round(tipBase * (Number(form.tip_choice) / 100) * 100) / 100
      : 0;
  // Chicago sales tax (10.25%) applies to prepared food only (after discount); tips are not taxed.
  const tax = subtotal > 0 ? Math.round((subtotal - discount) * CHICAGO_SALES_TAX_RATE * 100) / 100 : 0;
  const total = subtotal - discount + tax + tip + delivery;

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!emailRe.test(form.email.trim())) e.email = 'Valid email required';
    if (!phoneRe.test(form.phone.trim())) e.phone = 'Valid phone required';
    if (!form.date) e.date = 'Event date required';
    else if (form.date < todayStr()) e.date = 'Event date must be today or later';
    if (!form.headcount || Number(form.headcount) < 1) e.headcount = 'Headcount required';
    if (form.fulfillment === 'delivery' && (!form.delivery_address.trim() || form.delivery_address.trim().length < 5))
      e.delivery_address = 'Delivery address required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submitOrder = async () => {
    if (!validate()) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        fulfillment: form.fulfillment,
        contact: {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          company: form.company.trim() || null,
        },
        event: {
          date: form.date,
          time: form.time,
          headcount: Number(form.headcount),
          delivery_address: form.fulfillment === 'delivery' ? form.delivery_address.trim() : null,
          dietary_notes: form.dietary_notes.trim() || null,
        },
        items: cart.map((c) => ({
          id: String(c.id),
          name: c.name,
          unit_price: Number(c.price),
          qty: c.qty,
          modifiers: c.modifiers || null,
        })),
        tip,
      };
      const { data } = await axios.post(`${API}/orders`, payload, { timeout: 15000 });
      setConfirmation(data);
      setStep('payment');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Something went wrong';
      toast.error(typeof msg === 'string' ? msg : 'Order failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const onPaymentSuccess = () => {
    toast.success('Payment received! Order confirmed.');
    setStep('done');
  };

  const startOver = () => {
    setStep('cart');
    setConfirmation(null);
    setErrors({});
    setForm({ name: '', email: '', phone: '', company: '', date: '', time: '12:00', headcount: '', fulfillment: 'delivery', delivery_address: '', dietary_notes: '', tip_choice: 'none', tip_custom: '' });
    clearCart?.();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md bg-[#FFFBF5] border-l border-[#D6CFB8] flex flex-col">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl text-[#1B2A22] flex items-center gap-2">
            {step === 'cart' && (<><ShoppingBag className="w-5 h-5 text-[#245A40]" /> Your order</>)}
            {step === 'details' && (<><Calendar className="w-5 h-5 text-[#245A40]" /> Event details</>)}
            {step === 'payment' && (<><CreditCard className="w-5 h-5 text-[#245A40]" /> Payment</>)}
            {step === 'done' && (<><CheckCircle2 className="w-5 h-5 text-[#245A40]" /> Order received</>)}
          </SheetTitle>
        </SheetHeader>

        {step === 'cart' && (
          <CartStep
            cart={cart}
            updateQty={updateQty}
            subtotal={subtotal}
            tax={tax}
            delivery={delivery}
            total={total}
            fulfillment={form.fulfillment}
            belowMin={belowMin}
            onContinue={() => setStep('details')}
            onClear={() => cart.forEach((c) => updateQty(c.id, -c.qty))}
          />
        )}

        {step === 'details' && (
          <DetailsStep
            form={form}
            errors={errors}
            setField={setField}
            subtotal={subtotal}
            discount={discount}
            tip={tip}
            tax={tax}
            delivery={delivery}
            total={total}
            submitting={submitting}
            onBack={() => setStep('cart')}
            onSubmit={submitOrder}
          />
        )}

        {step === 'payment' && confirmation && (
          <PaymentStep
            order={confirmation}
            onBack={() => setStep('details')}
            onSuccess={onPaymentSuccess}
          />
        )}

        {step === 'done' && confirmation && (
          <DoneStep confirmation={confirmation} onDone={() => { onOpenChange(false); startOver(); }} />
        )}
      </SheetContent>
    </Sheet>
  );
}

// ============ Step components ============

function CartStep({ cart, updateQty, subtotal, tax, delivery, total, fulfillment, belowMin, onContinue, onClear }) {
  return (
    <>
      <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-4">
        {cart.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#ECE7D6] grid place-items-center">
              <ShoppingBag className="w-7 h-7 text-[#6F6A55]" />
            </div>
            <p className="mt-4 text-[#3F4A42]">Your order is empty. Add some bowls to get started.</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {cart.map((item) => (
              <li key={item.id + JSON.stringify(item.modifiers || '')} className="bg-white rounded-2xl p-3 border border-[#D6CFB8] flex gap-3">
                <img src={item.image} alt={item.name} className={item.image === '/images/logo.png' ? 'w-20 h-20 rounded-xl object-contain p-2 bg-[#E6EBD5]' : 'w-20 h-20 rounded-xl object-cover bg-[#E6EBD5]'} />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base text-[#1B2A22] leading-snug line-clamp-2">{item.name}</div>
                  <div className="text-xs text-[#6F6A55] mt-0.5">${item.price.toFixed(2)} • {item.serves}</div>
                  {item.modifiers && Object.values(item.modifiers).some((a) => a.length) && (
                    <div className="text-[10px] text-[#245A40] mt-1 leading-tight">
                      {Object.values(item.modifiers).flat().slice(0, 4).join(' • ')}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2 border border-[#D6CFB8] rounded-full p-0.5">
                      <button onClick={() => updateQty(item.id, -1)} className="w-7 h-7 rounded-full grid place-items-center hover:bg-[#ECE7D6]"><Minus className="w-3.5 h-3.5" /></button>
                      <span className="text-sm font-semibold w-5 text-center">{item.qty}</span>
                      <button onClick={() => updateQty(item.id, 1)} className="w-7 h-7 rounded-full grid place-items-center hover:bg-[#ECE7D6]"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="font-semibold text-[#1B2A22]">${(item.price * item.qty).toFixed(2)}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {cart.length > 0 && (
        <SheetFooter className="flex-col gap-3 mt-4 sm:flex-col">
          <div className="w-full space-y-2 text-sm">
            <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
            <Row label="Sales tax (10.25% Chicago)" value={`$${tax.toFixed(2)}`} />
            <div className="flex items-center justify-between text-[#245A40] font-semibold" data-testid="free-delivery-callout">
              <span>Free delivery included on all catering orders</span>
              <span>Free</span>
            </div>
            <div className="h-px bg-[#D6CFB8] my-2" />
            <Row label="Total" value={`$${total.toFixed(2)}`} bold />
          </div>
          {belowMin && (
            <div className="w-full text-xs px-3 py-2 rounded-lg bg-[#FCEEDB] text-[#9A3A0A] flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5" />
              <span>Add ${(MIN_ORDER - subtotal).toFixed(2)} more to meet the ${MIN_ORDER} catering minimum.</span>
            </div>
          )}
          <div className="w-full text-xs px-3 py-2 rounded-lg bg-[#E6EBD5] text-[#1B4631]" data-testid="group-discount-hint">
            Feeding 50 or more? A 10% Large Group Discount is applied automatically at checkout.
          </div>
          <Button disabled={belowMin} onClick={onContinue} className="btn-primary w-full h-12 rounded-full font-semibold text-base disabled:opacity-50" data-testid="continue-to-details-btn">
            Continue to event details
          </Button>
          <button onClick={onClear} className="text-xs text-[#6F6A55] hover:text-[#245A40] inline-flex items-center gap-1.5 self-center">
            <Trash2 className="w-3.5 h-3.5" /> Clear cart
          </button>
        </SheetFooter>
      )}
    </>
  );
}

function DetailsStep({ form, errors, setField, subtotal, discount, tip, tax, delivery, total, submitting, onBack, onSubmit }) {
  return (
    <>
      <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-4 space-y-5">
        <p className="text-sm text-[#3F4A42] leading-relaxed">
          We need 24 hours’ notice on all catering orders. We’ll confirm availability within 1 hour of submission.
        </p>

        <Section title="Contact">
          <FieldRow>
            <Field label="Name*" error={errors.name}><Input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Full name" /></Field>
            <Field label="Company" hint="(optional)"><Input value={form.company} onChange={(e) => setField('company', e.target.value)} placeholder="Company name" /></Field>
          </FieldRow>
          <FieldRow>
            <Field label="Email*" error={errors.email}><Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="you@example.com" /></Field>
            <Field label="Phone*" error={errors.phone}><Input type="tel" value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="773-555-0123" /></Field>
          </FieldRow>
        </Section>

        <Section title="Delivery or pickup">
          <div className="grid grid-cols-2 gap-2" data-testid="fulfillment-toggle">
            <button
              type="button"
              onClick={() => setField('fulfillment', 'delivery')}
              data-testid="fulfillment-delivery-btn"
              className={`rounded-xl border p-3 text-left transition-colors ${form.fulfillment === 'delivery' ? 'border-[#245A40] bg-[#E6EBD5]' : 'border-[#D6CFB8] bg-white hover:border-[#245A40]'}`}
            >
              <div className="text-sm font-semibold text-[#1B2A22]">Delivery</div>
              <div className="text-[11px] text-[#6F6A55] mt-0.5">Free — includes setup</div>
            </button>
            <button
              type="button"
              onClick={() => setField('fulfillment', 'pickup')}
              data-testid="fulfillment-pickup-btn"
              className={`rounded-xl border p-3 text-left transition-colors ${form.fulfillment === 'pickup' ? 'border-[#245A40] bg-[#E6EBD5]' : 'border-[#D6CFB8] bg-white hover:border-[#245A40]'}`}
            >
              <div className="text-sm font-semibold text-[#1B2A22]">Pickup</div>
              <div className="text-[11px] text-[#6F6A55] mt-0.5">Free — West Town, Chicago</div>
            </button>
          </div>
        </Section>

        <Section title="Event">
          <FieldRow>
            <Field label="Date*" error={errors.date}><Input type="date" min={todayStr()} value={form.date} onChange={(e) => setField('date', e.target.value)} /></Field>
            <Field label="Time*"><Input type="time" value={form.time} onChange={(e) => setField('time', e.target.value)} /></Field>
          </FieldRow>
          <FieldRow>
            <Field label="Headcount*" error={errors.headcount} hint="(50+ saves 10%)"><Input type="number" min="1" value={form.headcount} onChange={(e) => setField('headcount', e.target.value)} placeholder="e.g. 20" /></Field>
          </FieldRow>
          {form.fulfillment === 'delivery' ? (
            <Field label="Delivery address*" error={errors.delivery_address}>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6F6A55]" />
                <Input value={form.delivery_address} onChange={(e) => setField('delivery_address', e.target.value)} placeholder="Street, suite, city, ZIP" className="pl-9" />
              </div>
            </Field>
          ) : (
            <div className="text-xs text-[#3F4A42] bg-[#E6EBD5]/60 border border-[#CFDAB1] rounded-xl p-3 flex items-start gap-1.5" data-testid="pickup-note">
              <MapPin className="w-3.5 h-3.5 mt-0.5 text-[#245A40] shrink-0" />
              <span>Free pickup from our West Town kitchen, Chicago (60612). We&rsquo;ll confirm the exact pickup time by phone.</span>
            </div>
          )}
          <Field label="Dietary notes" hint="(optional)">
            <Textarea value={form.dietary_notes} onChange={(e) => setField('dietary_notes', e.target.value)} placeholder="Allergies, vegan needs, gluten-free requests, etc." rows={3} />
          </Field>
        </Section>

        <Section title="Add a tip">
          <p className="text-xs text-[#6F6A55] -mt-1 mb-2">Optional — 100% goes to the team preparing and delivering your order.</p>
          <div className="grid grid-cols-5 gap-2" data-testid="tip-options">
            {[
              { key: 'none', label: 'None' },
              { key: '10', label: '10%' },
              { key: '15', label: '15%' },
              { key: '20', label: '20%' },
              { key: 'custom', label: 'Custom' },
            ].map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => setField('tip_choice', o.key)}
                data-testid={`tip-${o.key}-btn`}
                className={`rounded-xl border px-2 py-2.5 text-sm font-semibold transition-colors ${form.tip_choice === o.key ? 'border-[#245A40] bg-[#E6EBD5] text-[#1B2A22]' : 'border-[#D6CFB8] bg-white text-[#3F4A42] hover:border-[#245A40]'}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          {form.tip_choice === 'custom' && (
            <div className="relative mt-2">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#6F6A55]">$</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.tip_custom}
                onChange={(e) => setField('tip_custom', e.target.value)}
                placeholder="Enter tip amount"
                className="pl-7"
                data-testid="tip-custom-input"
              />
            </div>
          )}
        </Section>
      </div>

      <SheetFooter className="flex-col gap-3 mt-4 sm:flex-col">
        <div className="w-full space-y-1.5 text-sm border-t border-[#D6CFB8] pt-3">
          <div className="flex items-center justify-between">
            <span className="text-[#3F4A42]">Subtotal</span>
            <span className="text-[#1B2A22]">${subtotal.toFixed(2)}</span>
          </div>
          {discount > 0 && (
            <div className="flex items-center justify-between" data-testid="group-discount-row">
              <span className="text-[#245A40] font-semibold">Large Group Discount (50+ guests, 10%)</span>
              <span className="text-[#245A40] font-semibold">-${discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-[#3F4A42]">Sales tax (10.25% Chicago)</span>
            <span className="text-[#1B2A22]">${tax.toFixed(2)}</span>
          </div>
          {tip > 0 && (
            <div className="flex items-center justify-between" data-testid="tip-row">
              <span className="text-[#3F4A42]">Tip{['10','15','20'].includes(form.tip_choice) ? ` (${form.tip_choice}%)` : ''}</span>
              <span className="text-[#1B2A22]">${tip.toFixed(2)}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-[#245A40] font-semibold" data-testid="details-free-delivery">
            <span>Free delivery included on all catering orders</span>
            <span>Free</span>
          </div>
          <div className="h-px bg-[#D6CFB8] my-1.5" />
          <div className="flex items-center justify-between">
            <span className="font-semibold text-[#1B2A22]">Total</span>
            <span className="font-display text-xl text-[#1B2A22]">${total.toFixed(2)}</span>
          </div>
        </div>
        <div className="flex gap-2 w-full">
          <Button onClick={onBack} variant="ghost" className="btn-ghost rounded-full h-12 px-4 font-semibold border border-[#1B2A22]/15 flex-1">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
          <Button onClick={onSubmit} disabled={submitting} className="btn-primary rounded-full h-12 font-semibold flex-[2] disabled:opacity-60" data-testid="continue-to-payment-btn">
            {submitting ? 'Submitting…' : `Continue to payment • $${total.toFixed(2)}`}
          </Button>
        </div>
      </SheetFooter>
    </>
  );
}

function PaymentStep({ order, onBack, onSuccess }) {
  const cardRef = useRef(null);
  const containerRef = useRef(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [initError, setInitError] = useState(null);
  const [paying, setPaying] = useState(false);

  // Load Square Web Payments SDK
  useEffect(() => {
    if (window.Square) { setSdkReady(true); return; }
    const existing = document.querySelector(`script[src="${SQ_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => setSdkReady(true));
      existing.addEventListener('error', () => setInitError('Could not load payment SDK'));
      if (window.Square) setSdkReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = SQ_SDK_URL;
    script.async = true;
    script.onload = () => setSdkReady(true);
    script.onerror = () => setInitError('Could not load payment SDK');
    document.body.appendChild(script);
  }, []);

  // Initialize card once SDK loaded & container present
  useEffect(() => {
    let cancelled = false;
    let cardInstance = null;
    async function init() {
      if (!sdkReady || !window.Square || !containerRef.current) return;
      if (!SQ_APP_ID || !SQ_LOCATION_ID) {
        setInitError('Payment is not configured. Please contact us to complete your order.');
        return;
      }
      try {
        const payments = window.Square.payments(SQ_APP_ID, SQ_LOCATION_ID);
        cardInstance = await payments.card();
        await cardInstance.attach(containerRef.current);
        if (cancelled) { try { await cardInstance.destroy(); } catch (_e) { /* noop */ } return; }
        cardRef.current = cardInstance;
        setCardReady(true);
      } catch (err) {
        console.error('Square init failed:', err);
        setInitError('Unable to initialize card form. Please refresh and try again.');
      }
    }
    init();
    return () => {
      cancelled = true;
      const c = cardRef.current;
      if (c) {
        try { c.destroy(); } catch (_e) { /* noop */ }
        cardRef.current = null;
      }
    };
  }, [sdkReady]);

  const pay = useCallback(async () => {
    if (!cardRef.current) return;
    setPaying(true);
    try {
      const result = await cardRef.current.tokenize();
      if (result.status !== 'OK') {
        const errMsg = result.errors?.map((e) => e.message).join(', ') || 'Card details invalid';
        toast.error(errMsg);
        setPaying(false);
        return;
      }
      const { data } = await axios.post(
        `${API}/payments/catering`,
        { source_id: result.token, order_id: order.id },
        { timeout: 20000 },
      );
      if (data?.status === 'COMPLETED' || data?.status === 'APPROVED') {
        onSuccess();
      } else {
        toast.error(`Payment status: ${data?.status || 'unknown'}`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Payment failed';
      toast.error(typeof msg === 'string' ? msg : 'Payment failed. Please try again.');
    } finally {
      setPaying(false);
    }
  }, [order.id, onSuccess]);

  return (
    <>
      <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-4 space-y-4">
        <div className="bg-white rounded-2xl p-4 border border-[#D6CFB8]">
          <div className="text-[10px] uppercase tracking-widest text-[#245A40] font-bold mb-2">Order summary</div>
          <div className="text-sm text-[#3F4A42] space-y-1">
            <div className="flex justify-between"><span>Order</span><span className="font-mono text-[#1B2A22]">#{order.id.slice(0, 8)}</span></div>
            <div className="flex justify-between"><span>Event</span><span className="text-[#1B2A22]">{order.event.date} · {order.event.time}</span></div>
            <div className="flex justify-between"><span>Subtotal</span><span className="text-[#1B2A22]">${order.subtotal.toFixed(2)}</span></div>
            {order.discount > 0 && (
              <div className="flex justify-between" data-testid="payment-discount-row"><span className="text-[#245A40] font-semibold">Large Group Discount (50+)</span><span className="text-[#245A40] font-semibold">-${order.discount.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between"><span>Sales tax (10.25%)</span><span className="text-[#1B2A22]">${order.tax.toFixed(2)}</span></div>
            {order.tip > 0 && (
              <div className="flex justify-between" data-testid="payment-tip-row"><span>Tip</span><span className="text-[#1B2A22]">${order.tip.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between text-[#245A40] font-semibold"><span>Free delivery included</span><span>Free</span></div>
            <div className="h-px bg-[#D6CFB8] my-2" />
            <div className="flex justify-between"><span className="font-semibold text-[#1B2A22]">Total</span><span className="font-display text-xl text-[#1B2A22]">${order.total.toFixed(2)}</span></div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 border border-[#D6CFB8]">
          <div className="text-[10px] uppercase tracking-widest text-[#245A40] font-bold mb-2 flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Secure card payment
          </div>
          <div
            ref={containerRef}
            id="w360-square-card-container"
            className="min-h-[90px]"
            data-testid="square-card-container"
          />
          {!cardReady && !initError && (
            <div className="text-xs text-[#6F6A55] mt-2">Loading secure card form…</div>
          )}
          {initError && (
            <div className="text-xs text-[#A4471A] mt-2 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5" /> {initError}
            </div>
          )}
          {SQ_ENV === 'sandbox' && (
            <div className="text-[11px] text-[#6F6A55] mt-3 leading-relaxed">
              <span className="font-semibold text-[#245A40]">Sandbox mode.</span> Test with card
              <span className="font-mono"> 4111 1111 1111 1111</span>, any future date, any CVV/ZIP.
            </div>
          )}
        </div>
      </div>

      <SheetFooter className="flex-col gap-2 mt-4 sm:flex-col">
        <div className="flex gap-2 w-full">
          <Button onClick={onBack} variant="ghost" className="btn-ghost rounded-full h-12 px-4 font-semibold border border-[#1B2A22]/15 flex-1" disabled={paying} data-testid="payment-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
          <Button
            onClick={pay}
            disabled={!cardReady || paying}
            className="btn-primary rounded-full h-12 font-semibold flex-[2] disabled:opacity-60"
            data-testid="pay-now-btn"
          >
            {paying ? 'Processing…' : `Pay $${order.total.toFixed(2)}`}
          </Button>
        </div>
      </SheetFooter>
    </>
  );
}

function DoneStep({ confirmation, onDone }) {
  return (
    <>
      <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-4">
        <div className="bg-[#E6EBD5] rounded-3xl p-6 sm:p-8 border border-[#CFDAB1] text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#245A40] grid place-items-center">
            <CheckCircle2 className="w-7 h-7 text-[#F6F2E8]" />
          </div>
          <div className="font-display text-2xl text-[#1B2A22] mt-4">Payment confirmed!</div>
          <p className="text-sm text-[#3F4A42] mt-2">
            Your card was charged and the catering order is locked in. We&rsquo;ll email a receipt shortly and
            confirm final logistics within 1 hour.
          </p>
          <div className="mt-4 text-xs text-[#6F6A55]">
            Order ID <span className="font-mono text-[#1B2A22]">{confirmation.id.slice(0, 8)}</span>
          </div>
        </div>
        <div className="mt-5 bg-white rounded-2xl p-4 border border-[#D6CFB8] text-sm">
          <div className="font-semibold text-[#1B2A22] mb-2">Summary</div>
          <div className="space-y-1 text-[#3F4A42]">
            <div>Event: {confirmation.event.date} at {confirmation.event.time}</div>
            <div>Headcount: {confirmation.event.headcount}</div>
            <div>
              {confirmation.fulfillment === 'pickup'
                ? 'Pickup: West Town kitchen, Chicago (60612)'
                : `Delivery: ${confirmation.event.delivery_address}`}
            </div>
            <div>Total: <span className="font-semibold text-[#1B2A22]">${confirmation.total.toFixed(2)}</span></div>
          </div>
        </div>
      </div>
      <SheetFooter className="mt-4">
        <Button onClick={onDone} className="btn-primary w-full h-12 rounded-full font-semibold">Done</Button>
      </SheetFooter>
    </>
  );
}

// ============ Small helpers ============

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? 'font-semibold text-[#1B2A22]' : 'text-[#3F4A42]'}>{label}</span>
      <span className={bold ? 'font-display text-xl text-[#1B2A22]' : 'text-[#1B2A22]'}>{value}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#245A40] font-bold mb-2">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function FieldRow({ children }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, hint, error, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-[#1B2A22] flex items-center gap-1">
        {label} {hint && <span className="text-[10px] font-normal text-[#6F6A55]">{hint}</span>}
      </Label>
      <div className="mt-1">{children}</div>
      {error && <div className="text-[11px] text-[#A4471A] mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {error}</div>}
    </div>
  );
}
